import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import analytics from '../services/analytics.service';

/**
 * useAnalytics Hook
 *
 * Verantwortlich für:
 * - Pageview-Tracking bei Route-Wechsel (manuell, weil send_page_view: false)
 * - User-Id (SHA-256-hashed Firebase UID) nach Login
 * - User-Properties (user_level, is_creator, is_promoter, signup_cohort)
 * - User-Id-Reset bei Logout
 *
 * Wird einmal in App.jsx gemountet (innerhalb AuthProvider + BrowserRouter).
 */
export default function useAnalytics() {
  const location = useLocation();
  const { user } = useAuth();
  const lastTrackedUid = useRef(null);

  // Pageview bei jedem Pfadwechsel (inkl. erstem Mount)
  useEffect(() => {
    analytics.track('page_view', {
      page_path: location.pathname + location.search,
      page_title: document.title
    });
  }, [location.pathname, location.search]);

  // User-Id + User-Properties synchronisieren
  useEffect(() => {
    const uid = user?.firebaseUid || null;

    if (uid && uid !== lastTrackedUid.current) {
      lastTrackedUid.current = uid;
      analytics.setUserId(uid);
      analytics.setUserProperties({
        user_level: user.level || 1,
        is_creator: user.role === 'seller' || user.role === 'both',
        is_promoter:
          user.role === 'buyer' || user.role === 'promoter' || user.role === 'both',
        signup_cohort: cohortFromCreatedAt(user.created_at)
      });
    } else if (!uid && lastTrackedUid.current) {
      lastTrackedUid.current = null;
      analytics.setUserId(null);
    }
  }, [user]);
}

function cohortFromCreatedAt(createdAt) {
  if (!createdAt) return null;
  const d = new Date(createdAt);
  if (isNaN(d.getTime())) return null;
  const yyyy = d.getUTCFullYear();
  const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
  return `${yyyy}-${mm}`;
}
